// Думите около черновата на всяка операция — ключове в `inventoryPage`
// (i18n), не текстове. Родът е различен („незавършената доставка",
// „незавършеното изписване"), затова всеки тип има собствения си набор;
// общите части са споделени ключове. Параметърът на заглавията с етикет е
// `label` — за старите ключове на доставката и инвентаризацията същата
// стойност се подава и като `supplier` / `warehouse` (`labelParams`).
import type { DraftType } from "../../../../api/inventoryDrafts";

export type DraftTexts = {
  // Началният екран
  sectionTitle: string;
  sectionHint: string;
  // празно място „Нямате незавършена …" с бутона „+ Нова"; без него секцията
  // се показва само когато има чернова (Изписване и Прехвърляне имат свое
  // „начало" отдолу — бързото начало / „Прехвърлени днес")
  sectionNone?: string;
  newLabel: string;
  continueLabel: string;
  labelFallback: string;
  // броят редове на картата и във въпросите: „3 продукта" / „12 преброени продукта"
  linesLabel: string;
  hasDraft: string;
  // „+ Нова" при незавършена моя
  askTitle: string;
  askTitleWithLabel?: string;
  askText: string;
  askCaption: string;
  askContinue: string;
  askContinueHint: string;
  askDiscard: string;
  askDiscardHint: string;
  // заета чернова на колега
  lockTitle: string;
  lockText: string;
  takeOverToast: string;
  takeOverFailed: string;
  // „Изтрий черновата" във футъра на листа
  deleteTitle: string;
  deleteTitleWithLabel?: string;
  deleteText: string;
  // въпросът на листа, отворен без избор, при намерена чернова
  promptTitle: string;
  promptTitleBy: string;
  promptContinueHint: string;
};

const key = (name: string) => `inventoryPage.${name}`;

// Общото за изписване и прехвърляне (среден род)
const NEUTER = {
  askCaption: key("dr6AskCaptionNeut"),
  askContinue: key("dr6ContinueDraftNeut"),
  askContinueHint: key("dr6AskContinueHintNeut"),
  askDiscard: key("dr6DiscardAndNewNeut"),
  askDiscardHint: key("cfmDraftAskDiscardHint"),
  lockText: key("dr6LockTextNeut"),
  takeOverFailed: key("dr6TakeOverFailed"),
  deleteText: key("dr6DeleteDraftText"),
  labelFallback: key("dr6NoWarehouse"),
  linesLabel: key("homeDraftLines"),
  promptContinueHint: key("cfmDraftContinueHint"),
};

export const DRAFT_TEXTS: Record<DraftType, DraftTexts> = {
  delivery: {
    sectionTitle: key("homeMyDraftTitle"),
    sectionHint: key("homeMyDraftHint"),
    sectionNone: key("homeNoDraft"),
    newLabel: key("newDeliveryPlus"),
    continueLabel: key("homeContinueDelivery"),
    labelFallback: key("homeNoSupplier"),
    linesLabel: key("homeDraftLines"),
    hasDraft: key("homeHasDraft"),
    askTitle: key("cfmDraftAskDeliveryTitlePlain"),
    askTitleWithLabel: key("cfmDraftAskDeliveryTitle"),
    askText: key("cfmDraftAskDeliveryText"),
    askCaption: key("cfmDraftAskCaption"),
    askContinue: key("homeContinueDraft"),
    askContinueHint: key("cfmDraftAskContinueHint"),
    askDiscard: key("homeDiscardAndNew"),
    askDiscardHint: key("cfmDraftAskDiscardHint"),
    lockTitle: key("cfmLockTitle"),
    lockText: key("cfmLockText"),
    takeOverToast: key("homeTakeOverToast"),
    takeOverFailed: key("homeTakeOverFailed"),
    deleteTitle: key("cfmDeleteDraftTitle"),
    deleteTitleWithLabel: key("cfmDeleteDraftTitleSupplier"),
    deleteText: key("cfmDeleteDraftText"),
    promptTitle: key("cfmDraftDeliveryTitle"),
    promptTitleBy: key("cfmDraftDeliveryTitleBy"),
    promptContinueHint: key("cfmDraftContinueHint"),
  },
  writeoff: {
    ...NEUTER,
    sectionTitle: key("dr6WoDraftTitle"),
    sectionHint: key("dr6WoDraftHint"),
    newLabel: key("newWriteoffPlus"),
    continueLabel: key("dr6WoContinue"),
    hasDraft: key("dr6WoHasDraft"),
    askTitle: key("dr6WoAskTitle"),
    askText: key("dr6WoAskText"),
    lockTitle: key("dr6WoLockTitle"),
    takeOverToast: key("dr6WoTakeOverToast"),
    deleteTitle: key("dr6WoDeleteTitle"),
    promptTitle: key("dr6WoAskTitle"),
    promptTitleBy: key("dr6WoPromptTitleBy"),
  },
  transfer: {
    ...NEUTER,
    sectionTitle: key("dr6TrxDraftTitle"),
    sectionHint: key("dr6TrxDraftHint"),
    newLabel: key("newTransferPlus"),
    continueLabel: key("dr6TrxContinue"),
    hasDraft: key("dr6TrxHasDraft"),
    askTitle: key("dr6TrxAskTitle"),
    askText: key("dr6TrxAskText"),
    lockTitle: key("dr6TrxLockTitle"),
    takeOverToast: key("dr6TrxTakeOverToast"),
    deleteTitle: key("dr6TrxDeleteTitle"),
    promptTitle: key("dr6TrxAskTitle"),
    promptTitleBy: key("dr6TrxPromptTitleBy"),
  },
  stocktake: {
    sectionTitle: key("stxDraftTitle"),
    sectionHint: key("dr6StkDraftHint"),
    sectionNone: key("stocktakeHomeNoDraft"),
    newLabel: key("newStocktakePlus"),
    continueLabel: key("stocktakeHomeContinue"),
    labelFallback: key("dr6NoWarehouse"),
    linesLabel: key("dr6StkCountedLines"),
    hasDraft: key("stocktakeHomeHasDraft"),
    askTitle: key("cfmDraftAskStocktakeTitlePlain"),
    askTitleWithLabel: key("cfmDraftAskStocktakeTitle"),
    askText: key("dr6StkAskText"),
    askCaption: key("cfmDraftAskCaption"),
    askContinue: key("homeContinueDraft"),
    askContinueHint: key("cfmDraftAskStocktakeContinueHint"),
    askDiscard: key("homeDiscardAndNew"),
    askDiscardHint: key("cfmDraftAskDiscardHint"),
    lockTitle: key("dr6StkLockTitle"),
    lockText: key("cfmLockText"),
    takeOverToast: key("dr6StkTakeOverToast"),
    takeOverFailed: key("dr6TakeOverFailed"),
    deleteTitle: key("dr6StkDeleteTitle"),
    deleteTitleWithLabel: key("dr6StkDeleteTitleLabel"),
    deleteText: key("dr6DeleteDraftText"),
    promptTitle: key("cfmDraftStocktakeTitle"),
    promptTitleBy: key("cfmDraftStocktakeTitleBy"),
    promptContinueHint: key("cfmDraftStocktakeContinueHint"),
  },
};

// Етикетът под всичките имена, които ключовете ползват
export const labelParams = (label: string) => ({ label, supplier: label, warehouse: label });
